import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Upload, Check, X, Sparkles, Euro, Receipt, AlertCircle, FileText, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

const ESTADO_COLOR = {
  pendiente: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  aprobada: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  pagada: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rechazada: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function FacturasExternos() {
  const { hasRole, user } = useAuth();
  const isAdmin = hasRole("admin");
  const canUpload = isAdmin || hasRole("montador_externo");
  const [facturas, setFacturas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [meta, setMeta] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [estado, setEstado] = useState("");
  const [uploadDialog, setUploadDialog] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (estado) params.estado = estado;
      const calls = [
        api.get("/facturas-externos/meta"),
        api.get("/facturas-externos", { params }),
      ];
      if (isAdmin || hasRole("gestoria")) {
        calls.push(api.get("/facturas-externos/resumen", { params: { year } }));
      }
      const res = await Promise.all(calls);
      setMeta(res[0].data);
      setFacturas(res[1].data || []);
      if (res[2]) setResumen(res[2].data);
    } catch { toast.error("Error cargando facturas"); }
  }, [year, estado, isAdmin, hasRole]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 lg:p-6" data-testid="facturas-externos-page">
      <PageHeader
        title="Facturas de colaboradores externos"
        subtitle={isAdmin ? "Gestión de facturas + control de pagos" : "Sube tus facturas y consulta el estado"}
        action={
          canUpload ? (
            <Button onClick={() => setUploadDialog(true)}
              className="bg-violet-500 text-zinc-950 hover:bg-violet-400" data-testid="fac-subir">
              <Sparkles className="w-4 h-4 mr-2" /> Subir factura con IA
            </Button>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 px-3 py-1 rounded border border-zinc-800 bg-zinc-900/40" data-testid="fac-readonly-badge">
              Sólo lectura
            </span>
          )
        }
      />

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-28" data-testid="fac-year"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Estado</Label>
          <Select value={estado || "__ALL__"} onValueChange={(v) => setEstado(v === "__ALL__" ? "" : v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-48" data-testid="fac-estado"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="__ALL__">Todos</SelectItem>
              {meta && Object.entries(meta.labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs admin */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="fac-totales">
          <KPI icon={Receipt} label="Facturas" value={resumen.totales.facturas} />
          <KPI icon={Euro} label="Facturado total" value={`${resumen.totales.facturado.toFixed(2)} €`} color="zinc" />
          <KPI icon={Check} label="Pagado" value={`${resumen.totales.pagado.toFixed(2)} €`}
            sub={`${resumen.totales.facturas_pagadas} facturas`} color="emerald" />
          <KPI icon={AlertCircle} label="Pendiente de pago" value={`${resumen.totales.pendiente.toFixed(2)} €`}
            sub={`${resumen.totales.facturas_pendientes} facturas`} color="amber" />
          <KPI icon={X} label="Rechazado" value={`${resumen.totales.rechazado.toFixed(2)} €`} color="red" />
        </div>
      )}

      {/* Top emisores (admin) */}
      {resumen && resumen.emisores.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden mb-4" data-testid="fac-emisores">
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            Inversión por colaborador
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2.5">Emisor</th>
                <th className="text-left p-2.5">NIF</th>
                <th className="text-right p-2.5">Facturas</th>
                <th className="text-right p-2.5">Total</th>
                <th className="text-right p-2.5">Pagado</th>
                <th className="text-right p-2.5">Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {resumen.emisores.map((e) => (
                <tr key={e.emisor_key} className="border-b border-zinc-800/60 hover:bg-zinc-900/40">
                  <td className="p-2.5 text-zinc-100">{e.emisor_nombre || "—"}</td>
                  <td className="p-2.5 font-mono text-xs text-zinc-400">{e.emisor_nif || "—"}</td>
                  <td className="p-2.5 text-right font-mono text-zinc-300">{e.facturas}</td>
                  <td className="p-2.5 text-right font-mono text-zinc-100">{e.total.toFixed(2)} €</td>
                  <td className="p-2.5 text-right font-mono text-emerald-300">{e.pagado.toFixed(2)} €</td>
                  <td className={`p-2.5 text-right font-mono ${e.pendiente > 0 ? "text-amber-300 font-semibold" : "text-zinc-500"}`}>
                    {e.pendiente.toFixed(2)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Listado facturas */}
      <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          {isAdmin ? `Facturas (${facturas.length})` : `Mis facturas (${facturas.length})`}
        </div>
        {facturas.length === 0 ? (
          <div className="text-center py-12 text-zinc-500" data-testid="fac-vacio">
            Sin facturas. Pulsa "Subir factura con IA" para empezar.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2.5">Fecha</th>
                <th className="text-left p-2.5">Emisor</th>
                <th className="text-left p-2.5">Número</th>
                <th className="text-left p-2.5">Concepto</th>
                <th className="text-right p-2.5">Base</th>
                <th className="text-right p-2.5">Total</th>
                <th className="text-left p-2.5">Estado</th>
                <th className="p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {facturas.map((f) => (
                <tr key={f.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`fac-${f.id}`}>
                  <td className="p-2.5 font-mono text-xs text-zinc-300">{f.fecha_emision}</td>
                  <td className="p-2.5 text-zinc-100">{f.emisor_nombre}<div className="text-[10px] text-zinc-500 font-mono">{f.emisor_nif}</div></td>
                  <td className="p-2.5 font-mono text-xs text-zinc-400">{f.numero || "—"}</td>
                  <td className="p-2.5 text-xs text-zinc-400 max-w-[200px] truncate">{f.concepto || "—"}</td>
                  <td className="p-2.5 text-right font-mono text-zinc-300">{(f.base_imponible || 0).toFixed(2)} €</td>
                  <td className="p-2.5 text-right font-mono text-zinc-100 font-semibold">{(f.total || 0).toFixed(2)} €</td>
                  <td className="p-2.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${ESTADO_COLOR[f.estado]}`}>
                      {meta?.labels[f.estado] || f.estado}
                    </span>
                  </td>
                  <td className="p-2.5 text-right">
                    {f.pdf_url && (
                      <a href={f.pdf_url} target="_blank" rel="noopener noreferrer"
                        className="text-amber-300 hover:text-amber-200 text-xs mr-2"
                        data-testid={`fac-ver-${f.id}`}>Ver</a>
                    )}
                    {isAdmin && f.estado !== "pagada" && (
                      <Button size="sm" variant="ghost" onClick={() => setResolveDialog(f)}
                        className="h-7 text-xs text-emerald-300 hover:bg-emerald-500/10"
                        data-testid={`fac-resolver-${f.id}`}>
                        <CreditCard className="w-3 h-3 mr-1" /> Gestionar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <FacturaUploadDialog
        open={uploadDialog} onClose={() => setUploadDialog(false)}
        onSaved={load} user={user}
      />
      <ResolverDialog
        factura={resolveDialog} onClose={() => setResolveDialog(null)}
        onSaved={load} meta={meta}
      />
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color }) {
  const txt = {
    amber: "text-amber-300", emerald: "text-emerald-300", red: "text-red-300", zinc: "text-zinc-100",
  }[color] || "text-zinc-100";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-xl font-semibold mt-1 ${txt}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function FacturaUploadDialog({ open, onClose, onSaved, user }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");

  const parse = async () => {
    if (!file) return toast.error("Selecciona archivo");
    setParsing(true);
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/facturas-externos/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      if (data.error) {
        toast.error(`IA: ${data.error}`);
      } else {
        setExtracted(data);
        toast.success("Datos extraídos con IA — revisa y confirma");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error procesando");
    } finally { setParsing(false); }
  };

  const save = async () => {
    if (!extracted?.emisor_nombre || !extracted?.fecha_emision) {
      return toast.error("Faltan campos obligatorios (emisor + fecha)");
    }
    setSaving(true);
    try {
      // Subir PDF a Cloudinary si está disponible
      let pdfUrlFinal = pdfUrl;
      let publicId;
      if (file && !pdfUrlFinal) {
        try {
          const { uploadToCloudinary } = await import("@/lib/cloudinary");
          const up = await uploadToCloudinary(file, "dnf/facturas-externos", "raw");
          pdfUrlFinal = up.secure_url;
          publicId = up.public_id;
        } catch (e) {
          // Permitir guardar sin URL si Cloudinary falla
          console.warn("Cloudinary failed", e);
        }
      }
      await api.post("/facturas-externos", {
        emisor_nombre: extracted.emisor_nombre,
        emisor_nif: extracted.emisor_nif,
        numero: extracted.numero,
        fecha_emision: extracted.fecha_emision,
        fecha_vencimiento: extracted.fecha_vencimiento,
        concepto: extracted.concepto,
        base_imponible: parseFloat(extracted.base_imponible || 0),
        iva_porcentaje: extracted.iva_porcentaje ? parseFloat(extracted.iva_porcentaje) : null,
        iva_importe: parseFloat(extracted.iva_importe || 0),
        irpf_porcentaje: extracted.irpf_porcentaje ? parseFloat(extracted.irpf_porcentaje) : null,
        irpf_importe: parseFloat(extracted.irpf_importe || 0),
        total: parseFloat(extracted.total || 0),
        pdf_url: pdfUrlFinal,
        public_id: publicId,
        parsed_by_ai: true,
        raw_ai_data: extracted,
      });
      toast.success("Factura registrada");
      onSaved();
      onClose();
      setFile(null);
      setExtracted(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !parsing && !saving && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-factura">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" /> Subir factura con Gemini IA
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Sube PDF, JPG o PNG de la factura. La IA extrae automáticamente todos los datos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Archivo</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { setFile(e.target.files[0]); setExtracted(null); }}
              className="bg-zinc-900 border-zinc-800 file:text-zinc-300" data-testid="factura-file" />
            {file && <div className="text-xs text-zinc-400 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>

          {file && !extracted && (
            <Button onClick={parse} disabled={parsing}
              className="w-full bg-violet-500 text-zinc-950 hover:bg-violet-400" data-testid="factura-parse">
              {parsing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extrayendo con IA…</> :
                <><Sparkles className="w-4 h-4 mr-2" /> Extraer datos con IA</>}
            </Button>
          )}

          {extracted && (
            <div className="space-y-2 bg-zinc-900/60 border border-zinc-800 rounded p-3" data-testid="factura-extracted">
              <div className="text-[10px] uppercase tracking-wider text-violet-300">✨ Revisar datos extraídos</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Emisor" value={extracted.emisor_nombre || ""} onChange={(v) => setExtracted({...extracted, emisor_nombre: v})} />
                <Field label="NIF" value={extracted.emisor_nif || ""} onChange={(v) => setExtracted({...extracted, emisor_nif: v})} />
                <Field label="Número" value={extracted.numero || ""} onChange={(v) => setExtracted({...extracted, numero: v})} />
                <Field label="Fecha emisión" type="date" value={extracted.fecha_emision || ""} onChange={(v) => setExtracted({...extracted, fecha_emision: v})} />
                <Field label="Base imponible" type="number" value={extracted.base_imponible || ""} onChange={(v) => setExtracted({...extracted, base_imponible: v})} />
                <Field label="IVA %" type="number" value={extracted.iva_porcentaje || ""} onChange={(v) => setExtracted({...extracted, iva_porcentaje: v})} />
                <Field label="IRPF %" type="number" value={extracted.irpf_porcentaje || ""} onChange={(v) => setExtracted({...extracted, irpf_porcentaje: v})} />
                <Field label="Total" type="number" value={extracted.total || ""} onChange={(v) => setExtracted({...extracted, total: v})} />
              </div>
              <div>
                <Label className="text-xs">Concepto</Label>
                <Textarea value={extracted.concepto || ""}
                  onChange={(e) => setExtracted({...extracted, concepto: e.target.value})}
                  className="bg-zinc-900 border-zinc-800 min-h-[50px] text-xs" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={parsing || saving}
            className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          {extracted && (
            <Button onClick={save} disabled={saving} className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400" data-testid="factura-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> :
                <><Check className="w-4 h-4 mr-2" /> Guardar factura</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="text-[10px]">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
    </div>
  );
}

function ResolverDialog({ factura, onClose, onSaved, meta }) {
  const [estado, setEstado] = useState("aprobada");
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().slice(0, 10));
  const [refPago, setRefPago] = useState("");
  const [comentario, setComentario] = useState("");

  useEffect(() => {
    if (factura) {
      setEstado(factura.estado === "pendiente" ? "aprobada" : "pagada");
      setComentario(factura.comentario_admin || "");
      setRefPago(factura.referencia_pago || "");
      setFechaPago(factura.fecha_pago || new Date().toISOString().slice(0, 10));
    }
  }, [factura?.id]);

  const submit = async () => {
    try {
      await api.patch(`/facturas-externos/${factura.id}/resolver`, {
        estado,
        fecha_pago: estado === "pagada" ? fechaPago : null,
        referencia_pago: estado === "pagada" ? refPago : null,
        comentario_admin: comentario || null,
      });
      toast.success(`Factura ${estado}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <Dialog open={!!factura} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-resolver">
        <DialogHeader>
          <DialogTitle>Gestionar factura</DialogTitle>
        </DialogHeader>
        {factura && (
          <div className="space-y-3">
            <div className="text-sm text-zinc-300 bg-zinc-900/60 p-2 rounded border border-zinc-800">
              <b>{factura.emisor_nombre}</b> · {factura.fecha_emision} · <b>{factura.total.toFixed(2)} €</b>
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="resolver-estado"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {meta && Object.entries(meta.labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {estado === "pagada" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Fecha pago</Label>
                  <Input type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)}
                    className="bg-zinc-900 border-zinc-800" data-testid="resolver-fecha-pago" />
                </div>
                <div>
                  <Label className="text-xs">Ref. pago (transferencia/bizum)</Label>
                  <Input value={refPago} onChange={(e) => setRefPago(e.target.value)}
                    placeholder="Ref. bancaria"
                    className="bg-zinc-900 border-zinc-800" data-testid="resolver-ref-pago" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Comentario</Label>
              <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)}
                className="bg-zinc-900 border-zinc-800 min-h-[50px]" data-testid="resolver-comentario" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="resolver-submit">
            <Check className="w-4 h-4 mr-2" /> Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
